// PN1 / DC-4 — REGISTERING THE WORKER, AND THE THREE PIECES THAT LIVE ON THE PAGE SIDE.
//
// The decisions are all in `src/pwaUpdate.js` (shared, gate-run). This file is the DOM writer that
// carries them out, in the shape `src/lib/downloads.ts` established: pure law elsewhere, browser
// calls here.
//
// WHAT IT DOES, IN HER WORDS:
//   • registers the worker (and on a browser without one, does nothing at all — no throw, no log
//     spam, no degraded site: the whole site works exactly as it does today without this file);
//   • ASKS THE BROWSER TO LOOK FOR A NEW VERSION on navigation and on foreground (piece three),
//     floored so a burst of taps is not a burst of requests;
//   • raises the VISIBLE notice when a new worker is waiting (piece four) and, only when she taps
//     Refresh, tells it to take over (piece one) and reloads ONCE.
//
// THE RELOAD GUARD IS NOT OPTIONAL. `controllerchange` fires on the new worker taking control, and
// reloading without a latch is the classic infinite refresh loop — the site reloading forever on her
// phone, which is a worse failure than never updating at all.
//
// R80: THE LATCH WAS NECESSARY AND NOT SUFFICIENT, AND THE HALF IT WAS MISSING WAS LIVE ON EVERY
// FIRST VISIT. `controllerchange` also fires the first time a worker CLAIMS a page that never had
// one, where nothing was replaced and there is nothing to reload for — measured at 12 of 12 fresh
// contexts. `shouldReloadOnControllerChange` is that decision, and it lives in `pwaUpdate.js` with
// the rest of the law so a gate RUNS it rather than reading this file. The fact it turns on —
// **was this page controlled when it loaded** — has to be read AT REGISTRATION, because once the
// event fires `navigator.serviceWorker.controller` is already set and the question is unaskable.
import { SKIP_WAITING, mayCheckNow, shouldReloadOnControllerChange } from "../pwaUpdate.js";

type Listener = (waiting: boolean) => void;

let registration: ServiceWorkerRegistration | null = null;
let listener: Listener | null = null;
let lastCheck = 0;
let reloading = false;

const supported = () => typeof navigator !== "undefined" && "serviceWorker" in navigator;

function announce() {
  listener?.(Boolean(registration?.waiting));
}

// Called by the app once, with the callback that raises the notice.
export function watchForUpdates(onWaiting: Listener) {
  listener = onWaiting;
  announce();
}

export function registerServiceWorker() {
  if (!supported()) return;
  // A worker registered before the page has settled competes with the page's own first loads. This
  // costs nothing and removes the whole class.
  window.addEventListener("load", () => {
    // READ FIRST, BEFORE ANYTHING CAN CLAIM THIS PAGE. A page that loaded with no controller is a
    // first visit; a later `controllerchange` on it is the first claim, not an update (R80). It is
    // then CARRIED FORWARD rather than fixed at load, so her Refresh still reloads a page that was
    // claimed for the first time earlier in this same visit.
    let wasControlled = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      registration = reg;
      announce();
      // A worker that arrives while she is here: watch it to the point where it is WAITING, which is
      // the moment there is something to tell her about.
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        next?.addEventListener("statechange", () => {
          if (next.state === "installed" && navigator.serviceWorker.controller) announce();
        });
      });
    }).catch(() => {
      // A registration that fails is not a member-facing event. The site is unchanged without it.
    });

    // THE RELOAD LATCH, AND THE QUESTION IN FRONT OF IT.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const reload = shouldReloadOnControllerChange({ wasControlled, alreadyReloading: reloading });
      wasControlled = true;   // Claimed or replaced, this page is controlled from here on.
      if (!reload) return;
      reloading = true;
      window.location.reload();
    });
  });
}

// PIECE THREE. `reason` is one of the law's: "navigation", "foreground", "manual".
export function checkForUpdate(reason: string) {
  if (!supported() || !registration) return;
  const now = Date.now();
  if (!mayCheckNow(reason, lastCheck, now)) return;
  lastCheck = now;
  registration.update().then(announce).catch(() => {});
}

// PIECE ONE, from her side: she tapped Refresh. The worker takes over and `controllerchange` does
// the reload, once.
export function applyUpdate() {
  const waiting = registration?.waiting;
  if (!waiting) {
    // Nothing is waiting any more (she left it long enough that it took over on its own reload).
    // A Refresh button that does nothing is a broken button, so it does the plain thing.
    window.location.reload();
    return;
  }
  waiting.postMessage({ type: SKIP_WAITING });
}

// >>> **Q3 (her order, 2026-10-04): "make sure the installed app always picks up new versions of the site after each deploy (no
// old saved copy shown)".** <<< An iPhone keeps an installed app frozen in memory when she switches away, so coming back shows
// the page from before the deploy until the app is closed for good. When the app comes back to the screen after at least
// 30 seconds away, it asks the server which version is live; if a newer one shipped, the page reloads (pages always load from
// the network first, so the reload is the new version). Under 30 seconds nothing happens, so a quick app switch never throws
// away what she was typing. This supersedes the older "she taps Refresh" rule for a new deploy, by her newer word.
const AWAY_MS = 30000;
let loadedCommit = "";
let hiddenAt = 0;
let movedOn = false; // set once a new deploy has been reloaded, so it reloads one time
const liveCommit = () => fetch("/api/version", { cache: "no-store", credentials: "same-origin" })
  .then((r) => (r.ok ? r.json() : null)).then((j) => String(j?.commit || "")).catch(() => "");
export function reloadOnNewDeploy() {
  if (typeof document === "undefined") return;
  void liveCommit().then((c) => { loadedCommit = c; });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
    if (!hiddenAt || Date.now() - hiddenAt < AWAY_MS || !loadedCommit) return;
    hiddenAt = 0;
    void liveCommit().then((c) => { if (c && c !== loadedCommit && !movedOn && !reloading) { movedOn = true; window.location.reload(); } });
  });
}
